import React, { Fragment, type PropsWithChildren, type ReactNode } from 'react';
import type { ProfilecardTriggerPosition, TriggerType } from '@atlaskit/profilecard/types';
import { useFlagsService } from '@atlassian/jira-flags';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { RouterActions } from '@atlassian/react-resource-router';
import { useIntl } from '@atlassian/jira-intl';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import {
	getNavigateToTeamsApp,
	isTeamsAppEnabled,
} from '@atlassian/jira-teams-app-config/src/index.tsx';
import { packageName, profileCardNextSourceName } from '../../common/constants.tsx';
import type { AnalyticsData, ProfileCardBaseProps } from '../../common/types.tsx';
import { ProfileCardWrapper } from '../../common/ui/profilecard-wrapper/main.tsx';
import { getSpaActions, getSpaActionsWithRouterPush } from '../../common/utils.tsx';
import { ProfileCardView } from './main.tsx';

const ConditionalErrorBoundary = componentWithFG(
	'empanada_profilecard_sync_trigger_import_final',
	({ children }: PropsWithChildren<{}>) => (
		<JSErrorBoundary id="profilecard" packageName={packageName} fallback="flag">
			{children}
		</JSErrorBoundary>
	),
	Fragment,
);

export type ProfileCardProps = ProfileCardBaseProps & {
	accountId: string;
	analyticsData?: AnalyticsData;
	children: ReactNode;
	position?: ProfilecardTriggerPosition;
	testId?: string;
	trigger?: TriggerType;
	ariaHideProfileTrigger?: boolean;
	isChildComment?: boolean;
	shouldTruncate?: boolean;
	showDelay?: number;
	hideDelay?: number;
	isVisible?: boolean;
};

export const ProfileCard = ({
	externalProfileClient,
	accountId,
	actions,
	analyticsData,
	children,
	position,
	testId,
	trigger,
	prepopulatedData,
	ariaHideProfileTrigger,
	isChildComment = false,
	shouldTruncate = false,
	showDelay,
	hideDelay,
	isVisible,
	hideAgentMoreActions,
}: ProfileCardProps) => {
	const intl = useIntl();
	const { showFlag, dismissFlag } = useFlagsService();
	const currentUserAccountId = useAccountId();

	const toTeamsAppUserProfile = (userId: string) =>
		getNavigateToTeamsApp({
			type: 'USER',
			payload: {
				userId,
			},
		}).onNavigate();

	return (
		<ConditionalErrorBoundary>
			<ContextualAnalyticsData
				sourceType={SCREEN}
				sourceName={profileCardNextSourceName}
				attributes={analyticsData}
			>
				<ProfileCardWrapper externalProfileClient={externalProfileClient}>
					{currentUserAccountId ? (
						<RouterActions>
							{({ push }) => (
								<ProfileCardView
									accountId={accountId}
									actions={
										fg('jsc_nin_smart_link_fast_follows')
											? getSpaActions(accountId, intl, actions)
											: getSpaActionsWithRouterPush(accountId, intl, push, actions)
									}
									position={position}
									onProfileClick={(profileAccountId) =>
										isTeamsAppEnabled()
											? toTeamsAppUserProfile(profileAccountId)
											: push(`/jira/people/${profileAccountId}`)
									}
									addFlag={showFlag}
									dismissFlag={dismissFlag}
									testId={testId}
									trigger={trigger}
									prepopulatedData={prepopulatedData}
									ariaHideProfileTrigger={ariaHideProfileTrigger}
									isChildComment={isChildComment}
									shouldTruncate={shouldTruncate}
									showDelay={showDelay}
									hideDelay={hideDelay}
									isVisible={isVisible}
									hideAgentMoreActions={hideAgentMoreActions}
								>
									{children}
								</ProfileCardView>
							)}
						</RouterActions>
					) : (
						children
					)}
				</ProfileCardWrapper>
			</ContextualAnalyticsData>
		</ConditionalErrorBoundary>
	);
};

export default ProfileCard;
